/**
 * TechniqueTable Component
 * 
 * Renders the welding technique table (QW-410) for WPS PDF documents.
 * Displays different technique parameters based on welding type:
 * 
 * For Groove welding:
 * - Process type and welding parameters for up to 3 processes
 * - Multiple/single pass and layer configurations
 * - Electrode spacing, angle, and multi-electrode settings
 * - Surface preparation, peening, oscillation settings
 * - Back gouging method and groove preparation details
 * 
 * For Overlay welding:
 * - Weld deposit chemistry and cleaning procedures
 * - Oscillation width, frequency, and filler metal delivery
 * - Surface preparation and peening specifications
 * 
 * Supports dynamic column sizing based on number of welding processes
 */

import { useMemo } from "react";

const TechniqueTable = ({ pdf, type = "Groove" }) => {
  const pdfData = pdf[0];
  const isOverlay = type === "Overlay";

  const { colwidth, col1Class, col2Class, col3Class, headerWidth } = useMemo(() => {
    const hasProcess2 = pdf[2] !== undefined;
    const hasProcess3 = pdf[3] !== undefined;

    let colwidth;
    if (hasProcess2) {
      colwidth = hasProcess3 ? "23%" : "35%";
    } else {
      colwidth = "70%";
    }

    let headerWidth;
    if (hasProcess2) {
      headerWidth = hasProcess3 ? "31%" : "30%";
    } else {
      headerWidth = "30%";
    }

    let col2Class;
    if (hasProcess2) {
      col2Class = hasProcess3 ? "WPSleft7-td" : "WPSleft7-td-noright";
    } else {
      col2Class = "WPStdhidden";
    }

    return {
      colwidth,
      headerWidth,
      col1Class: "WPSleft7-td-noleft",
      col2Class,
      col3Class: hasProcess3 ? "WPSleft7-td-noright1" : "WPStdhidden"
    };
  }, [pdf]);

  const processRows = [
    { label: "Multiple / Single Pass Per Side", field: "WPSD_WP_Multiple_SinglePassPerSide" },
    { label: "Multiple Or Single Layer", field: "WPSD_WP_Multiple_SingleLayer" },
    { label: "Multi / Single Electrode", field: "WPSD_WP_Multi_SingleElectrode" },
    { label: "Electrode Spacing Lateral", field: "WPSD_WP_ElectrodeSpacing_Late", unit: "mm" },
    { label: "Electrode Spacing Longitudinal", field: "WPSD_WP_ElectrodeSpacing_Logit", unit: "mm" },
    { label: "Electrode Angle", field: "WPSD_WP_ElectrodeAngleDegree", unit: "deg." }
  ];

  const singleColumnRows = [
    { label: "Supplementary MF Control", field: "WPSH_Tech_SupplementaryMFControl" },
    { label: "Peening", field: "WPSH_Tech_Peening" },
    { label: "Surface Preparation", field: "WPSH_Tech_SurfacePreparation" },
    { label: "Oscillation", field: "WPSH_Tech_Oscillation" },
    { label: "Initial Interpass Cleaning", field: "WPSH_Tech_Inital_InterpassCleaning" },
    { label: "Back Gouging Method", field: "WPSH_Tech_BackGougingMethod" },
    { label: "Groove Preparation", field: "WPSH_Tech_GroovePreparation" },
    { label: "Use of Thermal Process", field: "WPSH_Tech_UseOfThermalProcess" }
  ];

  const renderGrooveTable = () => (
    <table id="table" className="WPSTable7">
      <tr className="WPStrhide" style={{ borderTop: ".1px solid black" }}>
        <th className="WPSthhide" style={{ borderTop: ".1px solid black", width: headerWidth }}>Process-Type</th>
        <td className={col1Class} style={{ width: colwidth }}>
          {pdf[1]?.WPSD_WP_WeldingProcess} - {pdf[1]?.WPSD_Type}
        </td>
        <td className={col2Class} style={{ width: { colwidth } }}>
          {pdf[2] && pdf[2]?.WPSD_WP_WeldingProcess}
          {pdf[2] && " - "}
          {pdf[2] && pdf[2]?.WPSD_Type}
        </td>
        <td className={col3Class} style={{ width: { colwidth } }}>
          {pdf[3] && pdf[3]?.WPSD_WP_WeldingProcess}
          {pdf[3] && " - "}
          {pdf[3] && pdf[3]?.WPSD_Type}
        </td>
      </tr>

      {processRows.map((row) => (
        <tr key={row.field} className="WPStrhide">
          <th className="WPSthhide" style={{ width: headerWidth }}>
            {row.label}
            {row.unit && <div className="WPSrcorner">{row.unit}</div>}
          </th>
          <td className={col1Class} style={{ width: colwidth }}>{pdf[1]?.[row.field]}</td>
          <td className={col2Class} style={{ width: colwidth }}>{pdf[2]?.[row.field]}</td>
          <td className={col3Class} style={{ width: colwidth }}>{pdf[3]?.[row.field]}</td>
        </tr>
      ))}

      {singleColumnRows.map((row) => (
        <tr key={row.field} className="WPStrhide">
          <th className="WPSthhide" style={{ width: headerWidth }}>{row.label}</th>
          <td style={{ borderLeft: "none", width: "70%" }}>{pdfData?.[row.field]}</td>
        </tr>
      ))}

      <tr className="WPStrhide" style={{ borderBottom: "1px solid black" }}>
        <th className="WPSthhide" style={{ borderBottom: "1px solid black", width: headerWidth }}>Closed to out Chamber</th>
        <td style={{ borderLeft: "none", width: "70%" }}>{pdfData?.WPSH_Tech_ClosedtooutChamber}</td>
      </tr>
    </table>
  );


  const overlayRows = [
    { left: "Weld Deposit Chemistry", leftField: "WPSH_Tech_Weld_deposit_chemistry", right: "Initial/ Interpass Cleaning", rightField: "WPSH_Tech_Inital_InterpassCleaning", hasTopBorder: true },
    { left: "Supplementary MF Control", leftField: "WPSH_Tech_SupplementaryMFControl", right: "Surface Preparation", rightField: "WPSH_Tech_SurfacePreparation" },
    { left: "Oscillation", leftField: "WPSH_Tech_Oscillation", right: "Peening", rightField: "WPSH_Tech_Peening" },
    { left: "Oscillation Width(mm)", leftField: "WPSH_Tech_Oscillation_Width", right: "Filler Metal Delivery(GTAW)", rightField: "WPSH_Tech_Filler_Metal_Del" },
    { left: "Oscillation Frequency in CPM", leftField: "WPSH_Tech_Oscillation_Freq", right: "", rightField: "", hasBottomBorder: true }
  ];

  const renderOverlayTable = () => (
    <table id="table" className="WPSTable7">
      <tbody>
        {overlayRows.map((row) => (
          <tr key={row.leftField || row.left} className="WPStrhide">
            <th
              className="WPSthhide"
              style={{
                width: "22%",
                ...(row.hasTopBorder && { borderTop: "1px solid black" }),
                ...(row.hasBottomBorder && { borderBottom: "1px solid black" })
              }}
            >
              {row.left}
            </th>
            <td className={(() => {
              if (row.hasTopBorder) return "borderTop";
              if (row.hasBottomBorder) return "borderBottom";
              return "";
            })()}>
              {pdfData?.[row.leftField]}
            </td>
            <th
              className="WPSthhide"
              style={{
                width: "22%",
                ...(row.hasTopBorder && { borderTop: "1px solid black" }),
                ...(row.hasBottomBorder && { borderBottom: "1px solid black" })
              }}
            >
              {row.right}
            </th>
            <td className={(() => {
              if (row.hasTopBorder) return "borderTop";
              if (row.hasBottomBorder) return "borderBottom";
              return "";
            })()}>
              {row.rightField && pdfData?.[row.rightField]}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return isOverlay ? renderOverlayTable() : renderGrooveTable();
};

export default TechniqueTable;